import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Typography, Card, Tag } from 'antd';
import './index.css'; // 仅保留归档页专属样式，删除Header/Footer导入

const { Title, Text } = Typography;

export default function Archive({ posts: propsPosts }) {
  // 1. 统一数据处理（原有逻辑不变）
  const posts = useMemo(() => {
    const processed = Array.isArray(propsPosts) ? [...propsPosts] : [];
    // 强制ID为字符串，过滤有效文章（有ID+标题）
    return processed
      .map(post => ({ ...post, id: String(post.id || `post_${Date.now()}`) }))
      .filter(post => post.id && post.title);
  }, [propsPosts]);

  // 2. 按年月分组（原有逻辑不变，仅保留业务逻辑）
  const [groupedPosts, setGroupedPosts] = useState([]);
  useEffect(() => {
    if (!posts.length) {
      setGroupedPosts([]);
      return;
    }

    const groups = posts.reduce((acc, post) => {
      // 兼容createTime/date字段，容错无效日期
      const postDate = new Date(post.createTime || post.date || new Date());
      if (isNaN(postDate.getTime())) {
        postDate.setTime(new Date().getTime()); // 无效日期默认当前时间
      }

      const year = postDate.getFullYear();
      const month = postDate.getMonth() + 1;
      const groupKey = `${year}年${month}月`;

      if (!acc[groupKey]) {
        acc[groupKey] = {
          key: groupKey,
          year,
          month,
          count: 0,
          posts: []
        };
      }
      acc[groupKey].posts.push(post);
      acc[groupKey].count = acc[groupKey].posts.length;
      return acc;
    }, {});

    // 按时间倒序排序
    const sortedGroups = Object.values(groups).sort((a, b) => {
      return new Date(b.year, b.month) - new Date(a.year, a.month);
    });

    setGroupedPosts(sortedGroups);
  }, [posts]);

  // 调试日志（保留原有日志）
  useEffect(() => {
    console.log('【Archive有效文章】', posts.map(p => ({ id: p.id, title: p.title })));
    console.log('【Archive分组结果】', groupedPosts.map(g => ({ key: g.key, count: g.count })));
  }, [posts, groupedPosts]);

  return (
    <div className="archive-page" style={{ minHeight: 'calc(100vh - 160px)', padding: '24px 16px' }}>
      {/* 移除局部Header渲染，由App.js全局Header接管 */}

      {/* 归档页核心内容（样式保留，仅删除Header/Footer） */}
      <div className="archive-container">
        <Title level={2} className="archive-title">
          文章归档
        </Title>

        {groupedPosts.length === 0 ? (
          <div className="archive-empty-tip">
            <Text>暂无文章归档</Text>
          </div>
        ) : (
          groupedPosts.map((group) => (
            <Card
              key={group.key}
              className="archive-card"
              bordered={false}
            >
              <div className="archive-month-header">
                <div className="archive-month-name">
                  <span className="archive-month-icon">📅</span>
                  {group.key}
                </div>
                <span className="archive-month-count">共 {group.count} 篇</span>
              </div>

              <div className="month-posts">
                {group.posts.map((post) => (
                  <div
                    key={post.id}
                    className="archive-item"
                  >
                    <Link
                      to={`/post/${post.id}`}
                      className="archive-item-title"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {post.title}
                    </Link>

                    <div className="archive-item-meta">
                      <span>{new Date(post.createTime || post.date).toLocaleDateString()}</span>
                      {post.tags?.map((tag) => (
                        <Tag
                          key={`${post.id}-${tag}`}
                          className="archive-item-category"
                          bordered={false}
                        >
                          {tag}
                        </Tag>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))
        )}
      </div>

      {/* 移除局部Footer渲染，由App.js全局Footer接管 */}
    </div>
  );
}